// create-mode.ts — the Create-release dialog's MODE vms (owner-reported
// 2026-10-09, Basic Helm #6484). Pure: the dialog paints these, the suite
// drives them directly (the create-release.ts precedent).
//
// The owner opened Create release on qa: it defaulted to a FULL template,
// the preflight read `… 1 FAIL [FAIL dev-server.seat]`, Create stayed
// disabled, nothing said which templates are basic, and the "Upgrade corpus"
// toggle showed regardless. So:
//   - every template's label names the mode a cut from it freezes, read from
//     the list row's derived `resolved_mode` / `resolved_platform`
//     (server template-mode.ts, the cut's own readers);
//   - a basic pick hides the corpus-only options and says what a basic
//     release IS, in one line;
//   - a preflight FAIL says what to DO, not just the check name.
// The DEFAULT template is not chosen here (that is a config seat).

export type TemplateMode = 'full' | 'basic';

export interface TemplateModeRow {
  id: string;
  name: string;
  is_default: boolean;
  resolved_mode?: unknown;
  resolved_platform?: unknown;
}

/** The one line a basic pick shows where the corpus options were. */
export const BASIC_CREATE_LINE = 'Basic release: one agent workspace, files, and the test environment — no corpus';

/** The mode the server derived for a row; null = the server did not say
 * (an older console) — the label then stays exactly as before. */
export function templateModeOf(row: TemplateModeRow | null | undefined): TemplateMode | null {
  const m = row?.resolved_mode;
  return m === 'basic' || m === 'full' ? m : null;
}

/** The picker label with its mode: `<label> — basic (Windows)` / `<label> — full`. */
export function templateModeLabel(label: string, row: TemplateModeRow | null | undefined): string {
  const mode = templateModeOf(row);
  if (mode === null) return label;
  if (mode === 'full') return `${label} — full`;
  const p = row?.resolved_platform;
  const os = p === 'windows' ? 'Windows' : p === 'linux' ? 'Linux — refused: basic is Windows only' : 'platform unreadable';
  return `${label} — basic (${os})`;
}

/** The names of the basic templates in a list, in list order. */
export function basicTemplateNames(rows: TemplateModeRow[]): string[] {
  return rows.filter((r) => templateModeOf(r) === 'basic').map((r) => r.name);
}

/** What a preflight FAIL asks the user to DO. `dev-server.seat` on a template
 * that is not basic names the way out the owner did not see: a basic
 * template (when the list has one) or seating it. Every other FAIL: the
 * check's own server remedy (else its detail). */
export function createTodoText(
  fail: { check: string; detail: string; remedy?: string | null },
  ctx: { mode: TemplateMode | null; basicNames: string[] },
): string {
  const how = fail.remedy || fail.detail;
  if (fail.check === 'dev-server.seat' && ctx.mode !== 'basic') {
    return ctx.basicNames.length
      ? `This template needs a dev-server seat — choose a basic template (${ctx.basicNames.join(', ')}) or seat it: ${how}`
      : `This template needs a dev-server seat — seat it: ${how}`;
  }
  return `To create, fix ${fail.check}: ${how}`;
}
